import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./overridden-cost-value.i18n";

type OverriddenCostValueProps = {
  /** The value in force, formatted. */
  value: string;
  /** The catalogue value it replaced, formatted; shown struck through. */
  original: string;
  /** Advisor-only: the justification. Undefined on the client's reads. */
  note?: string | null;
};

/**
 * QL-20 — a fee the advisor overrode: the catalogue value struck through, then
 * the value in force. The advisor sees the note on hover; the client never does.
 */
export function OverriddenCostValue({ value, original, note }: OverriddenCostValueProps) {
  const t = useT(T);
  const content = (
    <span className="whitespace-nowrap">
      <span className="sr-only">{t.catalogueValue}: </span>
      <s className="mr-1 text-muted-foreground">{original}</s>
      <span className="font-semibold">{value}</span>
    </span>
  );
  if (note === undefined) return content;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help underline decoration-dotted underline-offset-4">
          {content}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-left">
        <p className="font-medium">{t.overriddenBy}</p>
        <p>
          {t.catalogueValue}: {original}
        </p>
        {note && (
          <p className="mt-1 whitespace-pre-wrap">
            {t.note}: {note}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
